import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import { RichTextEditor } from "../../../../components/field/richTextEditor/RichTextEditor";
import useTeamMemberQuery from "../../../../hooks/query/connections/useTeamMemberQuery";
import { formatEvidenceName } from "../../../../common/entities/EntityUtils";
import { Team } from "../../../../types/team";
import { TeamMember } from "../../../../types/teamMember";

interface TeamMembersViewSectionProps {
  team: Team;
}

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const displayEvidenceName = (
  evidence: TeamMember["name_internal_evidence"] | TeamMember["name_external_evidence"]
): string => {
  const formatted = formatEvidenceName(evidence);
  return formatted === "-" ? "N/A" : formatted;
};

const getMemberSubtitle = (member: TeamMember): string =>
  member?.role || member?.status || "Team Member";

const getTeamMemberFields = (member: TeamMember): ViewAccordionField[] => [
  {
    label: "Role",
    value: <Typography variant="body1">{displayText(member?.role)}</Typography>,
  },
  {
    label: "Status",
    value: <Typography variant="body1">{displayText(member?.status)}</Typography>,
  },
  {
    label: "Name Internal Evidence",
    value: (
      <Typography variant="body1">
        {displayEvidenceName(member?.name_internal_evidence)}
      </Typography>
    ),
  },
  {
    label: "Name External Evidence",
    value: (
      <Typography variant="body1">
        {displayEvidenceName(member?.name_external_evidence)}
      </Typography>
    ),
  },
  {
    label: "Notes",
    value: (
      <RichTextEditor
        value={typeof member?.notes === "string" ? member.notes : ""}
        readOnly
        fullWidth
        placeholder="N/A"
      />
    ),
  },
];

const TeamMembersViewSection: React.FC<TeamMembersViewSectionProps> = ({
  team,
}) => {
  const { useFetchConnectedEdges } = useTeamMemberQuery(team.system_language_type);
  const { data, isLoading, errorMessage } = useFetchConnectedEdges(
    team?._id,
    "outbound",
    {
      bodyParams: {
        system_language_type: team.system_language_type,
      },
    }
  );

  const items = useMemo<TeamMember[]>(() => data?.data ?? [], [data]);

  return (
    <ConnectionAccordionSection<TeamMember>
      title="Team Members"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(member, index) => member?._key ?? `team-member-${index}`}
      getItemTitle={(member) =>
        member?.toDoc ? (
          <EntityReferenceComponent entity={member.toDoc} linkMode="view" />
        ) : (
          <Typography variant="body1">N/A</Typography>
        )
      }
      getItemSubtitle={(member) => getMemberSubtitle(member)}
      getItemFields={(member) => getTeamMemberFields(member)}
      emptyText="No team members"
    />
  );
};

export default TeamMembersViewSection;
